// Agrupamento "inteligente" de canais — converte uma lista crua do Xtream
// (várias variantes do mesmo canal) num modelo de UI: 1 card por canal,
// com N fontes selecionáveis. Roda em cima do parseChannelName.
//
// Princípio conservador: na dúvida, NÃO agrupa. Falso-positivo (juntar PFC 1
// com PFC 2) é muito pior que falso-negativo (mostrar 2 Globos separadas).
import { CURATED_KEY_PREFIX, ISOLATED_KEY_PREFIX, UNNAMED_KEY_PREFIX } from './family-keys';
import { parsableIdOf, parsableNameOf, parseChannelName } from './name-parser';
// ===== Score =====
const QUALITY_SCORE = {
    '4K': 400,
    FHD: 300,
    HD: 200,
    SD: 100,
};
const CODEC_BONUS = {
    H265: 50, // metade do bitrate pra mesma qualidade — bom em redes ruins
    H264: 0,
};
const TAG_PENALTY = {
    backup: -200,
    test: -300,
    alt: -150,
    plus: -180, // canal "+" geralmente é fallback (não confundir com Disney+/ESPN+)
};
const BACKUP_TAGS = new Set(['backup', 'test', 'alt', 'plus']);
/**
 * Score combinado de uma variante. Mais alto = melhor candidato a primário.
 * Não é uma escala absoluta, é só pra ordenar dentro do mesmo grupo.
 */
function computeScore(parsed) {
    let score = 500; // baseline
    if (parsed.quality)
        score += QUALITY_SCORE[parsed.quality];
    if (parsed.codec)
        score += CODEC_BONUS[parsed.codec];
    for (const tag of parsed.tags) {
        score += TAG_PENALTY[tag] ?? 0;
    }
    return score;
}
function isBackupVariant(tags) {
    return tags.some((t) => BACKUP_TAGS.has(t));
}
// ===== Tier/Bandwidth → qualidade preferida =====
const TIER_MAX_QUALITY = {
    vlow: 'SD',
    low: 'HD',
    mid: 'FHD',
    high: '4K',
    ultra: '4K',
};
// Banda mínima sustentada pra cada qualidade rodar estável (Mbps).
// Conservador — IPTV tem stalling bem mais cedo que VOD adaptativo.
const QUALITY_MIN_MBPS = {
    SD: 1.5,
    HD: 4,
    FHD: 8,
    '4K': 18,
};
const QUALITY_ORDER = ['SD', 'HD', 'FHD', '4K'];
/** Maior qualidade que cabe na banda atual. null se sem dados de banda. */
function maxQualityForMbps(mbps) {
    if (mbps == null)
        return null;
    let best = null;
    for (const q of QUALITY_ORDER) {
        if (mbps >= QUALITY_MIN_MBPS[q])
            best = q;
    }
    return best;
}
function qualityRank(q) {
    if (!q)
        return -1;
    return QUALITY_ORDER.indexOf(q);
}
// ===== Group key (chave de agrupamento) =====
/**
 * Normaliza nome pra chave: lowercase + strip acentos + collapse spaces.
 * "TNT Series" e "TNT Séries" → mesma key. cleanName original preservado.
 */
function normalizeKey(s) {
    return s
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}
/**
 * Chave de agrupamento: cleanName normalizado + categoria.
 * Usar categoria evita juntar "Globo" de Esportes com "Globo" de Notícias
 * (raro, mas acontece em painéis com categorias temáticas mistas).
 */
function groupKey(cleanName, categoryId) {
    return `${categoryId}::${normalizeKey(cleanName)}`;
}
/** Acumula UM canal no estado de agrupamento (parse + key + source + merge). */
function accumulateChannel(ch, opts, state) {
    // O `id` é a IDENTIDADE do canal: é o que vira `ChannelSource.channelId`, o
    // que o player abre em `/player/:id` e o que "Recentes" grava no IDB. Sem id
    // utilizável não existe canal — só um card que não abre, um beco sem saída
    // pra quem navega de D-pad. Então normaliza pela mesma whitelist do nome e,
    // se não sobrar nada, o item é DESCARTADO (não vira grupo).
    //
    // Isto é o que fecha a classe que a guarda de nome sozinha deixou aberta:
    // `{}` é truthy, passava pelo `if (ch)` do caller, e a chave sintética
    // interpolava o id sujo virando a string LITERAL `'__unnamed__::undefined'`
    // — igual pra todos os quebrados, colapsando tudo de novo num grupo só. A
    // interpolação em template string é a armadilha: ela coage QUALQUER coisa em
    // texto e o resultado ainda parece uma chave legítima.
    const id = parsableIdOf(ch.id);
    if (!id)
        return;
    const parsed = parseChannelName(ch.rawName ?? ch.name);
    // Curadoria de família (override do backend) vence a heurística.
    //   - familyIsolated: canal não agrupa com ninguém → key único (id).
    //   - familyKey: identidade curada → agrupa quem compartilha o mesmo key.
    //   - sem override: heurística de nome (comportamento de hoje).
    //
    // `cleanName` VAZIO (nome cru null/ausente/só-espaços — ver `parsableNameOf`
    // em name-parser.ts) é o caso degenerado: sem override e sem nome, a chave
    // heurística viraria `''` (ou `cat::`) pra TODOS os sem-nome, colapsando
    // canais sem nenhuma relação num grupo só. Isso viola o princípio declarado
    // no topo deste arquivo ("na dúvida, NÃO agrupa") e some com canal da grade
    // (só o 1º vira card; o resto vira "fonte" escondida atrás do seletor,
    // contra "catálogo 100% navegável"). Então sem nome = grupo próprio.
    const key = ch.familyIsolated
        ? `${ISOLATED_KEY_PREFIX}${id}`
        : ch.familyKey
            ? `${CURATED_KEY_PREFIX}${ch.familyKey}`
            : !parsed.cleanName
                ? `${UNNAMED_KEY_PREFIX}${id}`
                : opts.ignoreCategory
                    ? normalizeKey(parsed.cleanName)
                    : groupKey(parsed.cleanName, ch.categoryId);
    // displayName: nome curado (renomear) > nome limpo da heurística.
    // Pode sair VAZIO aqui (sem nome e sem curadoria) — `finalizeGroups` é quem
    // garante a invariante "nenhum grupo sai com displayName vazio".
    const displayName = ch.familyName ?? parsed.cleanName;
    const source = {
        channelId: id,
        // `ChannelSource.rawName` é tipado `string` e alimenta o dedup de nome-cru
        // (`allSameRawName`) + tooltip de debug. Guardar o `null` do provedor aqui
        // seria propagar a mentira de tipo pra fora do pacote; normaliza pela MESMA
        // whitelist do parser.
        rawName: parsableNameOf(ch.rawName ?? ch.name),
        quality: parsed.quality,
        codec: parsed.codec,
        language: parsed.language,
        audio: parsed.audio,
        tags: parsed.tags,
        score: computeScore(parsed),
        isBackup: isBackupVariant(parsed.tags),
    };
    let g = state.groups.get(key);
    if (!g) {
        g = {
            key,
            displayName,
            categoryId: ch.categoryId,
            logo: ch.logo,
            epgChannelId: ch.epgChannelId,
            hasArchive: ch.hasArchive,
            archiveWindowHours: ch.archiveWindowHours,
            isAdult: ch.isAdult,
            sources: [source],
        };
        state.groups.set(key, g);
        state.order.push(key);
    }
    else {
        g.sources.push(source);
        // Logo: usa primeiro com logo de score maior
        if (!g.logo && ch.logo)
            g.logo = ch.logo;
        // EPG: primeiro que tiver
        if (!g.epgChannelId && ch.epgChannelId)
            g.epgChannelId = ch.epgChannelId;
        // Archive: agrega
        if (ch.hasArchive)
            g.hasArchive = true;
        if (ch.archiveWindowHours &&
            (!g.archiveWindowHours || ch.archiveWindowHours > g.archiveWindowHours)) {
            g.archiveWindowHours = ch.archiveWindowHours;
        }
        // Adulto: contagia o grupo
        if (ch.isAdult)
            g.isAdult = true;
    }
}
/**
 * Finaliza o agrupamento: ordena as sources de cada grupo e aplica o dedup de
 * nome-cru. Idempotente (source.sort é estável no resultado) → chamável em
 * snapshots progressivos e de novo no fim sem efeito colateral.
 */
function finalizeGroups(state) {
    // Ordena sources de cada grupo: primário (score desc) > backups
    for (const g of state.groups.values()) {
        g.sources.sort((a, b) => {
            // Backups SEMPRE depois de primários
            if (a.isBackup !== b.isBackup)
                return a.isBackup ? 1 : -1;
            return b.score - a.score;
        });
    }
    // Pós-processamento: alguns providers listam canais distintos com o MESMO
    // nome cru ("Brasileirão" repetido 40 vezes). Não dá pra distinguir pelo
    // nome, mas claramente não são variantes de qualidade. Detectamos isso:
    // se há MUITAS fontes (≥4) e TODAS com nome cru idêntico, desagrupamos.
    // Threshold conservador (4+) pra evitar quebrar duplicatas legítimas
    // entre categorias (ex: "Globo HD" listado em Abertos e Nacionais).
    const SAME_NAME_DUPE_THRESHOLD = 4;
    const finalGroups = [];
    // ===== Invariante: NENHUM grupo sai daqui com `displayName` vazio =====
    //
    // Um grupo sem rótulo vira um card em branco na grade — e N deles viram N
    // cards IDÊNTICOS e vazios, indistinguíveis pra quem só tem D-pad. Antes de
    // os sem-nome ganharem chave própria, o desempate abaixo mascarava isso por
    // acidente: todos caíam num grupo só, o dedup de nome-cru disparava e o
    // `${g.displayName} ${idx+1}` produzia " 1", " 2", " 3"… Ao dar chave própria
    // a cada sem-nome, cada grupo passou a ter 1 fonte, o desempate parou de
    // disparar e o rótulo virou "" pra todos. O de-colapso é uma correção real,
    // mas não podia levar a numeração embora — daí a numeração explícita aqui.
    //
    // Ancorado na INVARIANTE (`displayName` vazio), não na PROXY (chave
    // `__unnamed__::`): também pega os outros caminhos que produzem grupo sem
    // rótulo e que o de-colapso nunca cobriu — canal isolado por curadoria e sem
    // nome (`__isolated__::`, sempre 1 fonte) e família curada com `familyName`
    // string vazia (`isCurated` pula o desempate). Um só lugar fecha os três.
    //
    // Por que o NÚMERO e não um texto: este pacote é compartilhado (TV, API,
    // painel) e não tem locale — cravar "Canal sem nome" aqui seria string PT-BR
    // hardcoded no shared. E traduzir só na camada do TV não resolveria o P1:
    // "Canal sem nome" × N continua indistinguível. O que distingue é o ordinal.
    // Quem quiser o rótulo por extenso lê `unnamedOrdinal` e traduz no render.
    //
    // Contador sobre a ordem de EMISSÃO. `state.order` é append-only e o
    // desempate é determinístico, então o número de um grupo só muda se o grupo
    // ANTERIOR passar a se desdobrar — o que exige `stream_id` duplicado (ver
    // abaixo). No caso normal a numeração é estável entre os snapshots
    // progressivos do chunked. Idempotente: não muta `state.groups`.
    let unnamedSeq = 0;
    const labelled = (g) => {
        if (g.displayName)
            return g;
        unnamedSeq += 1;
        return { ...g, displayName: String(unnamedSeq), unnamedOrdinal: unnamedSeq };
    };
    for (const k of state.order) {
        const g = state.groups.get(k);
        if (!g)
            continue;
        // Grupo veio de override de família (curador disse "junta"): a heurística de
        // dedup por nome-cru NÃO pode desfazer a curadoria. Mantém intacto.
        const isCurated = k.slice(0, CURATED_KEY_PREFIX.length) === CURATED_KEY_PREFIX;
        if (!isCurated && g.sources.length >= SAME_NAME_DUPE_THRESHOLD && allSameRawName(g.sources)) {
            // Quebra em N grupos de 1 fonte, com nome numerado.
            //
            // Numera-se DEPOIS do desdobramento, um por filho. Fazer antes parecia
            // equivalente e não era: o pai recebia UM ordinal e o `{...g}` copiava o
            // MESMO valor pros N filhos. Isso é alcançável — basta `stream_id`
            // DUPLICADO (dado sujo de Xtream, exatamente o que esta entrega existe
            // pra sobreviver): os sem-nome caem na mesma chave `__unnamed__::<id>`,
            // o grupo junta 4+ fontes de rawName idêntico e desdobra. Hoje seria
            // inofensivo porque ninguém lê `unnamedOrdinal`, mas é bomba armada pro
            // follow-up: no dia em que a UI renderizar `t('...', { n })`, voltam N
            // rótulos IDÊNTICOS e a regressão que o board bloqueou renasce.
            //
            // O `|| ''` cobre o outro lado: com o pai sem rótulo, `${''} ${idx+1}`
            // daria " 1"/" 2" — truthy, escapando da invariante. Zerando, cada filho
            // cai no `labelled` e ganha o SEU número.
            g.sources.forEach((src, idx) => {
                finalGroups.push(labelled({
                    ...g,
                    key: `${g.key}::${idx + 1}`,
                    displayName: g.displayName ? `${g.displayName} ${idx + 1}` : '',
                    sources: [src],
                }));
            });
        }
        else {
            finalGroups.push(labelled(g));
        }
    }
    return finalGroups;
}
/**
 * Agrupa canais. Cada GroupedChannel tem N sources ordenados por score desc.
 * Preserva ordem original (primeira aparição define posição na lista).
 */
export function groupLiveChannels(channels, opts = {}) {
    const state = { groups: new Map(), order: [] };
    // `if (ch)` guarda slot vazio no array (item null de página ruim do
    // provedor). NÃO é zelo novo: `groupLiveChannelsChunked` já pulava o item
    // falsy no seu loop — a versão SÍNCRONA é que divergia e lançava, quebrando
    // a invariante "chunked === sync" que este arquivo declara e testa. Um slot
    // sem `id`/`name` não tem o que renderizar nem o que tocar; pular é a única
    // saída que preserva os outros 1.040 canais da lista.
    //
    // Só pega `null`/`undefined`: `{}` é TRUTHY e passa. Quem descarta o objeto
    // sem identidade é a guarda de `id` dentro do `accumulateChannel`.
    for (const ch of channels)
        if (ch)
            accumulateChannel(ch, opts, state);
    return finalizeGroups(state);
}
/** Teto de tempo (ms) que o loop segura a main thread antes de ceder. Mantém o
 *  maior "hold" ~1 frame INDEPENDENTE do device — na LG lenta os pedaços ficam
 *  menores automaticamente, então nunca vira o bloqueio de 5,7s. */
const DEFAULT_CHUNK_BUDGET_MS = 12;
function nowMs() {
    return typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? performance.now()
        : Date.now();
}
/**
 * Versão que NÃO trava a main thread: processa os canais em chunks, cedendo o
 * frame entre eles (via `onYield`), e emitindo snapshots progressivos (via
 * `onProgress`) pra a grade encher enquanto processa. O resultado FINAL é
 * idêntico ao de `groupLiveChannels` (mesmo núcleo). Resolve com `null` se
 * cancelado no meio.
 */
export async function groupLiveChannelsChunked(channels, opts, hooks) {
    const chunkSize = hooks.chunkSize && hooks.chunkSize > 0 ? hooks.chunkSize : 200;
    const budgetMs = hooks.budgetMs && hooks.budgetMs > 0 ? hooks.budgetMs : DEFAULT_CHUNK_BUDGET_MS;
    const state = { groups: new Map(), order: [] };
    let sinceYield = 0;
    let chunkStart = nowMs();
    for (let i = 0; i < channels.length; i++) {
        const ch = channels[i];
        if (ch)
            accumulateChannel(ch, opts, state);
        sinceYield += 1;
        // Cede quando o pedaço atinge o teto de ITENS OU estoura o teto de TEMPO
        // (o que vier primeiro). O teto de tempo é adaptativo: na LG lenta os
        // pedaços ficam menores sozinhos, mantendo o maior hold ~1 frame.
        const more = i + 1 < channels.length;
        if (more && (sinceYield >= chunkSize || nowMs() - chunkStart >= budgetMs)) {
            if (hooks.onProgress)
                hooks.onProgress(finalizeGroups(state));
            await hooks.onYield();
            if (hooks.isCancelled?.())
                return null;
            sinceYield = 0;
            chunkStart = nowMs();
        }
    }
    if (hooks.isCancelled?.())
        return null;
    return finalizeGroups(state);
}
function allSameRawName(sources) {
    if (sources.length === 0)
        return false;
    const first = sources[0].rawName;
    return sources.every((s) => s.rawName === first);
}
/**
 * Escolhe a melhor fonte de um grupo respeitando o tier do usuário.
 * Estratégia:
 * 1. Filtra fontes com qualidade ≤ tier (ou sem qualidade — desconhecida).
 * 2. Prefere a de maior score dentro do permitido.
 * 3. Se nenhuma se qualifica (todas acima do tier), pega a de menor qualidade.
 * 4. Se grupo só tem backups, retorna o melhor backup.
 */
export function pickBestSource(group, tier) {
    if (group.sources.length === 0)
        return null;
    if (group.sources.length === 1)
        return group.sources[0] ?? null;
    const maxQ = TIER_MAX_QUALITY[tier];
    const maxRank = QUALITY_ORDER.indexOf(maxQ);
    // Primários ≤ tier
    const primariesInTier = group.sources.filter((s) => !s.isBackup && (s.quality === null || qualityRank(s.quality) <= maxRank));
    if (primariesInTier.length > 0) {
        return primariesInTier[0] ?? null; // já está ordenado por score desc
    }
    // Nenhum primário cabe no tier — pega o de menor qualidade entre primários
    const primaries = group.sources.filter((s) => !s.isBackup);
    if (primaries.length > 0) {
        const sorted = [...primaries].sort((a, b) => qualityRank(a.quality) - qualityRank(b.quality));
        return sorted[0] ?? null;
    }
    // Só sobrou backup — devolve o melhor backup
    return group.sources[0] ?? null;
}
/**
 * Próxima fonte pra fallback (auto-switch quando a atual falha).
 * Pula a fonte atual e tenta a próxima do grupo. Retorna null se acabou.
 */
export function nextSource(group, currentChannelId) {
    const idx = group.sources.findIndex((s) => s.channelId === currentChannelId);
    if (idx === -1 || idx >= group.sources.length - 1)
        return null;
    return group.sources[idx + 1] ?? null;
}
/**
 * Versão rica que considera tier (hardware) E bandwidth (rede) — usa o
 * MENOR limite dos dois. Bandwidth costuma ser o gargalo real.
 */
export function pickBestSourceFor(group, opts) {
    if (group.sources.length === 0)
        return null;
    if (group.sources.length === 1)
        return group.sources[0] ?? null;
    const tierMax = TIER_MAX_QUALITY[opts.tier];
    const bwMax = maxQualityForMbps(opts.bandwidthMbps);
    // Limite efetivo = menor dos dois (mais restritivo)
    const effectiveMax = bwMax && QUALITY_ORDER.indexOf(bwMax) < QUALITY_ORDER.indexOf(tierMax) ? bwMax : tierMax;
    const maxRank = QUALITY_ORDER.indexOf(effectiveMax);
    const primariesInBudget = group.sources.filter((s) => !s.isBackup && (s.quality === null || qualityRank(s.quality) <= maxRank));
    if (primariesInBudget.length > 0)
        return primariesInBudget[0] ?? null;
    // Nenhum cabe — pega o de menor qualidade entre primários
    const primaries = group.sources.filter((s) => !s.isBackup);
    if (primaries.length > 0) {
        const sorted = [...primaries].sort((a, b) => qualityRank(a.quality) - qualityRank(b.quality));
        return sorted[0] ?? null;
    }
    return group.sources[0] ?? null;
}
//# sourceMappingURL=channel-grouping.js.map